<script setup>
// V1 Admin Intelligence Bridge (Part 1/10/11) — card-based editor for
// project.knowledge_base. No raw JSON is ever shown to the user; every
// section maps 1:1 to a key already read by ProjectKnowledgeGraphService.
// Autosave: each card/group saves itself (debounced) via a PATCH that only
// touches its own keys — sibling sections are never clobbered.
//
// M26.1 — UI-only responsive rework: CSS grid workspace, sticky search,
// section anchors, collapsible list sections, character counters, and
// keyboard navigation. No backend endpoint or payload shape changed.
import {
  reactive,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useRoute } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import projectKnowledgeAPI from 'dashboard/api/nexceo/projectKnowledge';
import KnowledgeSectionCard from './KnowledgeSectionCard.vue';
import { useProjectHeader } from 'dashboard/composables/nexceo/useProjectHeader';
import ProjectWorkspaceLayout from './ProjectWorkspaceLayout.vue';
import { projectWorkspaceTabs } from './projectWorkspaceTabs';

const route = useRoute();
const { accountId } = useAccount();
const projectId = computed(() => Number(route.params.id));

const { project } = useProjectHeader(projectId);
const workspaceTabs = computed(() =>
  projectWorkspaceTabs({
    standalone: true,
    id: route.params.id,
    accountId: accountId.value,
  })
);

const kb = reactive({
  usp: '',
  builder_name: '',
  builder_track_record: '',
  target_customer: '',
  lifestyle_notes: '',
  investment_notes: '',
  rental_notes: '',
  construction_highlights: '',
  legal_notes: '',
  competitor_notes: '',
  nearby_places: [],
  faqs: [],
  objection_responses: [],
  talking_points: [],
});

const isLoading = ref(true);
const loadError = ref(null);

// ── Autosave plumbing ──────────────────────────────────────────────────────
// status per group: idle | pending | saving | saved | error
const saveStatus = reactive({});
const lastSavedAt = reactive({});
const saveTimers = {};

async function doSave(group, fields) {
  saveStatus[group] = 'saving';
  const payload = {};
  fields.forEach(f => {
    payload[f] = kb[f];
  });
  try {
    await projectKnowledgeAPI.update(projectId.value, payload);
    saveStatus[group] = 'saved';
    lastSavedAt[group] = new Date();
    setTimeout(() => {
      if (saveStatus[group] === 'saved') saveStatus[group] = 'idle';
    }, 2000);
  } catch {
    saveStatus[group] = 'error';
  }
}

function scheduleSave(group, fields) {
  saveStatus[group] = 'pending';
  clearTimeout(saveTimers[group]);
  saveTimers[group] = setTimeout(() => doSave(group, fields), 700);
}

// Declarative simple-field groups — each card autosaves independently.
const FIELD_GROUPS = [
  { key: 'usp', fields: ['usp'] },
  { key: 'builder_story', fields: ['builder_name', 'builder_track_record'] },
  { key: 'target_customer', fields: ['target_customer'] },
  { key: 'lifestyle_notes', fields: ['lifestyle_notes'] },
  { key: 'investment', fields: ['investment_notes', 'rental_notes'] },
  { key: 'construction_highlights', fields: ['construction_highlights'] },
  { key: 'legal_notes', fields: ['legal_notes'] },
  { key: 'competitor_notes', fields: ['competitor_notes'] },
];

function onFieldInput(group) {
  const g = FIELD_GROUPS.find(x => x.key === group);
  scheduleSave(group, g.fields);
}

function onArrayChange(group) {
  scheduleSave(group, [group]);
}

// Flushes any pending nearby_places edit immediately (so a place's just-
// typed coordinates aren't lost/stale) before asking the backend to fetch
// real driving routes for every place that has coordinates — see
// Nexceo::ProjectKnowledgeController#recompute_routes.
const isRecomputingRoutes = ref(false);
const recomputeRoutesError = ref('');
async function recomputeRoutes() {
  recomputeRoutesError.value = '';
  if (saveStatus.nearby_places === 'pending') {
    clearTimeout(saveTimers.nearby_places);
    await doSave('nearby_places', ['nearby_places']);
  }
  isRecomputingRoutes.value = true;
  try {
    const { data } = await projectKnowledgeAPI.recomputeRoutes(projectId.value);
    kb.nearby_places = data.nearby_places ?? [];
  } catch (e) {
    recomputeRoutesError.value =
      e?.response?.data?.error ?? 'Failed to calculate routes.';
  } finally {
    isRecomputingRoutes.value = false;
  }
}

function charCount(value) {
  return (value || '').length;
}

// ── Nearby places categories (needed by the collapse-default calc below) ──
const NEARBY_CATEGORIES = [
  { value: 'school', label: 'Nearby Schools', icon: 'i-lucide-graduation-cap' },
  { value: 'hospital', label: 'Nearby Hospitals', icon: 'i-lucide-cross' },
  { value: 'office', label: 'Nearby Offices', icon: 'i-lucide-briefcase' },
  { value: 'it_park', label: 'Nearby IT Parks', icon: 'i-lucide-building-2' },
  {
    value: 'shopping',
    label: 'Nearby Shopping',
    icon: 'i-lucide-shopping-bag',
  },
  {
    value: 'connectivity',
    label: 'Connectivity',
    icon: 'i-lucide-train-front',
  },
];

function placesFor(category) {
  return kb.nearby_places.filter(p => p.category === category);
}

// Live collapsed/expanded state per list section — controlled here (not
// inside KnowledgeSectionCard) so that adding an item can force its section
// open. Sections already holding more than a few items start collapsed so
// the workspace stays scannable; empty/short sections start open so the
// "Add" action is immediately visible. Must prime before `isLoading` flips
// to false — component mount (which reads this as each card's initial
// collapsed state) is triggered by that same flag, so priming any later
// loses the race against Vue's render flush.
const collapsedState = reactive({});
function primeCollapsedState() {
  NEARBY_CATEGORIES.forEach(cat => {
    collapsedState[`nearby_${cat.value}`] = placesFor(cat.value).length > 3;
  });
  collapsedState.faqs = kb.faqs.length > 3;
  collapsedState.objection_responses = kb.objection_responses.length > 3;
  collapsedState.talking_points = kb.talking_points.length > 5;
}

function expandAllSections() {
  Object.keys(collapsedState).forEach(key => {
    collapsedState[key] = false;
  });
}

function collapseAllSections() {
  Object.keys(collapsedState).forEach(key => {
    collapsedState[key] = true;
  });
}

async function load() {
  isLoading.value = true;
  loadError.value = null;
  try {
    const { data } = await projectKnowledgeAPI.show(projectId.value);
    Object.assign(kb, data);
    primeCollapsedState();
  } catch {
    loadError.value = 'Failed to load project knowledge.';
  } finally {
    isLoading.value = false;
  }
}

// ── Nearby places (grouped by category) ─────────────────────────────────────
function addPlace(category) {
  kb.nearby_places.push({
    category,
    name: '',
    distance_km: '',
    travel_time_minutes: '',
    latitude: '',
    longitude: '',
    thumbnail_url: '',
  });
  collapsedState[`nearby_${category}`] = false;
  onArrayChange('nearby_places');
}

function removePlace(place) {
  const idx = kb.nearby_places.indexOf(place);
  if (idx !== -1) kb.nearby_places.splice(idx, 1);
  onArrayChange('nearby_places');
}

// ── FAQs ─────────────────────────────────────────────────────────────────
function addFaq() {
  kb.faqs.push({ category: '', question: '', answer: '' });
  collapsedState.faqs = false;
  onArrayChange('faqs');
}
function removeFaq(item) {
  const idx = kb.faqs.indexOf(item);
  if (idx !== -1) kb.faqs.splice(idx, 1);
  onArrayChange('faqs');
}

// ── Objection responses ─────────────────────────────────────────────────
function addObjection() {
  kb.objection_responses.push({ objection_type: '', response: '' });
  collapsedState.objection_responses = false;
  onArrayChange('objection_responses');
}
function removeObjection(item) {
  const idx = kb.objection_responses.indexOf(item);
  if (idx !== -1) kb.objection_responses.splice(idx, 1);
  onArrayChange('objection_responses');
}

// ── Talking points ───────────────────────────────────────────────────────
function addTalkingPoint() {
  kb.talking_points.push('');
  collapsedState.talking_points = false;
  onArrayChange('talking_points');
}
function removeTalkingPoint(idx) {
  kb.talking_points.splice(idx, 1);
  onArrayChange('talking_points');
}

// ── Section anchors + keyboard navigation (M26.1) ───────────────────────
const SECTION_ANCHORS = {
  usp: 'section-usp',
  builder_name: 'section-builder-story',
  builder_track_record: 'section-builder-story',
  target_customer: 'section-ideal-customer',
  lifestyle_notes: 'section-lifestyle',
  investment_notes: 'section-investment',
  rental_notes: 'section-investment',
  construction_highlights: 'section-construction',
  legal_notes: 'section-legal',
  competitor_notes: 'section-competitor',
  faqs: 'section-faqs',
  objection_responses: 'section-objections',
  talking_points: 'section-talking-points',
};

// Order matches the 4 visual groups in the template below (Project /
// Location / Sales / Legal Intelligence) so the pill nav and the page read
// the same way top-to-bottom.
const NAV_LINKS = [
  { anchor: 'section-usp', label: 'USP' },
  { anchor: 'section-builder-story', label: 'Builder Story' },
  { anchor: 'section-construction', label: 'Construction' },
  { anchor: 'section-nearby-school', label: 'Nearby Places' },
  { anchor: 'section-ideal-customer', label: 'Ideal Customer' },
  { anchor: 'section-lifestyle', label: 'Lifestyle' },
  { anchor: 'section-investment', label: 'Investment' },
  { anchor: 'section-faqs', label: 'FAQs' },
  { anchor: 'section-objections', label: 'Objections' },
  { anchor: 'section-talking-points', label: 'Talking Points' },
  { anchor: 'section-competitor', label: 'Competitor' },
  { anchor: 'section-legal', label: 'Legal' },
];

function anchorFor(result) {
  if (result.section === 'nearby_places') {
    return `section-nearby-${result.match.category}`;
  }
  return SECTION_ANCHORS[result.section] || null;
}

function jumpTo(anchorId) {
  if (!anchorId) return;
  const el = document.getElementById(anchorId);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  el.focus({ preventScroll: true });
}

// ── Scroll-spy: highlight the nav pill for whichever section is in view ──
// Nearby Places has one card per category (school/hospital/…) but only one
// nav pill, so every `section-nearby-*` id maps back to that single pill.
const activeAnchor = ref(NAV_LINKS[0]?.anchor ?? null);
let sectionObserver = null;

function navAnchorFor(sectionId) {
  return sectionId.startsWith('section-nearby-')
    ? 'section-nearby-school'
    : sectionId;
}

function setupScrollSpy() {
  const sections = document.querySelectorAll('[id^="section-"]');
  if (!sections.length) return;
  sectionObserver?.disconnect();
  sectionObserver = new IntersectionObserver(
    entries => {
      const visible = entries
        .filter(entry => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) {
        activeAnchor.value = navAnchorFor(visible[0].target.id);
      }
    },
    { rootMargin: '-140px 0px -65% 0px', threshold: 0 }
  );
  sections.forEach(el => sectionObserver.observe(el));
}

// ── Search (Part 11) ─────────────────────────────────────────────────────
const searchQuery = ref('');
const searchResults = ref(null);
const isSearching = ref(false);
const searchInputRef = ref(null);
let searchTimer = null;

function matchLabel(result) {
  if (result.section === 'nearby_places') return result.match.name;
  if (result.section === 'faqs') return result.match.question;
  if (result.section === 'objection_responses') return result.match.response;
  if (result.section === 'talking_points') return result.match;
  return result.match;
}

const SECTION_LABELS = {
  usp: 'Project USP',
  builder_name: 'Builder Story',
  builder_track_record: 'Builder Story',
  target_customer: 'Ideal Customer',
  lifestyle_notes: 'Lifestyle',
  investment_notes: 'Investment Highlights',
  rental_notes: 'Investment Highlights',
  construction_highlights: 'Construction Highlights',
  legal_notes: 'Legal Notes',
  competitor_notes: 'Competitor Notes',
  nearby_places: 'Nearby Places',
  faqs: 'FAQs',
  objection_responses: 'Common Objections',
  talking_points: 'Sales Talking Points',
};

async function runSearch() {
  clearTimeout(searchTimer);
  if (!searchQuery.value.trim()) {
    searchResults.value = null;
    return;
  }
  searchTimer = setTimeout(async () => {
    isSearching.value = true;
    try {
      const { data } = await projectKnowledgeAPI.search(
        projectId.value,
        searchQuery.value
      );
      searchResults.value = data.results;
    } finally {
      isSearching.value = false;
    }
  }, 300);
}

function onGlobalKeydown(e) {
  const tag = document.activeElement?.tagName;
  const isTyping = tag === 'INPUT' || tag === 'TEXTAREA';

  if (e.key === '/' && !isTyping) {
    e.preventDefault();
    searchInputRef.value?.focus();
    return;
  }
  if (e.key === 'Escape' && document.activeElement === searchInputRef.value) {
    searchQuery.value = '';
    searchResults.value = null;
    searchInputRef.value?.blur();
  }
}

onMounted(async () => {
  await load();
  document.addEventListener('keydown', onGlobalKeydown);
  await nextTick();
  setupScrollSpy();
});

onBeforeUnmount(() => {
  Object.values(saveTimers).forEach(clearTimeout);
  document.removeEventListener('keydown', onGlobalKeydown);
  sectionObserver?.disconnect();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <ProjectWorkspaceLayout
    :project="project"
    :tabs="workspaceTabs"
    active-tab-key="knowledge"
  >
    <div class="flex-1 overflow-y-auto">
      <!-- Sticky header: title, search, section anchors -->
      <div
        class="sticky top-0 z-10 bg-n-background border-b border-n-weak flex flex-col gap-3 px-4 py-3"
      >
        <div class="flex items-center justify-between gap-3 flex-wrap">
          <div class="flex items-center gap-2">
            <span class="i-lucide-brain size-4 text-woot-500" />
            <h2 class="text-sm font-semibold text-n-slate-12">
              Project Knowledge
            </h2>
          </div>
          <div class="relative w-full sm:w-96">
            <span
              class="absolute left-2.5 top-1/2 -translate-y-1/2 i-lucide-search size-3.5 text-n-slate-7 pointer-events-none"
            />
            <input
              ref="searchInputRef"
              v-model="searchQuery"
              type="text"
              placeholder="Search knowledge…"
              title="Search across USP, FAQs, nearby places, objections and more. Press / to focus, Esc to clear."
              class="w-full pl-8 pr-12 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
              @input="runSearch"
            />
            <kbd
              class="hidden sm:flex items-center justify-center absolute right-2 top-1/2 -translate-y-1/2 px-1.5 h-5 rounded border border-n-weak text-[10px] text-n-slate-7 pointer-events-none"
            >
              /
            </kbd>
          </div>
        </div>

        <!-- Section anchor nav -->
        <div v-if="!isLoading && !loadError" class="flex items-center gap-2">
          <nav
            class="flex items-center gap-1.5 overflow-x-auto pb-0.5 min-w-0"
            aria-label="Jump to knowledge section"
          >
            <button
              v-for="link in NAV_LINKS"
              :key="link.anchor"
              type="button"
              class="shrink-0 whitespace-nowrap px-2.5 py-1 text-xs rounded-full border transition-colors"
              :class="
                activeAnchor === link.anchor
                  ? 'border-woot-500 bg-woot-50 text-woot-600 dark:bg-woot-500/10 dark:text-woot-400'
                  : 'border-n-weak text-n-slate-9 hover:border-woot-400 hover:text-woot-600 dark:hover:text-woot-400'
              "
              @click="jumpTo(link.anchor)"
            >
              {{ link.label }}
            </button>
          </nav>
          <div class="flex items-center gap-1.5 shrink-0 ml-auto pl-2">
            <button
              type="button"
              class="flex items-center gap-1 whitespace-nowrap px-2.5 py-1 text-xs rounded-full border border-n-weak text-n-slate-9 hover:border-woot-400 hover:text-woot-600 dark:hover:text-woot-400 transition-colors"
              @click="expandAllSections"
            >
              <span class="i-lucide-chevrons-down-up size-3" />
              Expand All
            </button>
            <button
              type="button"
              class="flex items-center gap-1 whitespace-nowrap px-2.5 py-1 text-xs rounded-full border border-n-weak text-n-slate-9 hover:border-woot-400 hover:text-woot-600 dark:hover:text-woot-400 transition-colors"
              @click="collapseAllSections"
            >
              <span class="i-lucide-chevrons-up-down size-3" />
              Collapse All
            </button>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-4 p-4">
        <!-- Search results -->
        <div
          v-if="searchResults !== null"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-3"
        >
          <div class="flex items-center gap-2 mb-2">
            <span
              v-if="isSearching"
              class="i-lucide-loader-2 size-3.5 animate-spin text-n-slate-7"
            />
            <p class="text-xs font-medium text-n-slate-9">
              {{ searchResults.length }} result{{
                searchResults.length === 1 ? '' : 's'
              }}
              for "{{ searchQuery }}"
            </p>
          </div>
          <div v-if="!searchResults.length" class="text-xs text-n-slate-7">
            No matches. Try another term.
          </div>
          <ul v-else class="flex flex-col gap-1.5">
            <li v-for="(result, idx) in searchResults" :key="idx">
              <button
                type="button"
                class="flex items-start gap-2 w-full text-left px-2.5 py-1.5 rounded-lg bg-n-background border border-n-weak text-xs hover:border-woot-400 transition-colors"
                @click="jumpTo(anchorFor(result))"
              >
                <span
                  class="px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-8 shrink-0"
                >
                  {{ SECTION_LABELS[result.section] || result.section }}
                </span>
                <span class="text-n-slate-11 truncate flex-1">{{
                  matchLabel(result)
                }}</span>
                <span
                  class="i-lucide-arrow-right size-3 text-n-slate-6 shrink-0 mt-0.5"
                />
              </button>
            </li>
          </ul>
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="flex justify-center py-10">
          <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-7" />
        </div>

        <p
          v-else-if="loadError"
          class="text-xs text-ruby-600 dark:text-ruby-400 px-3 py-2 rounded-lg bg-ruby-50 dark:bg-ruby-500/10 border border-ruby-200 dark:border-ruby-500/30"
        >
          {{ loadError }}
        </p>

        <!-- Responsive workspace grid -->
        <div v-else class="grid grid-cols-1 xl:grid-cols-2 gap-4 items-stretch">
          <!-- ══ Group: Project Intelligence ══ -->
          <div class="col-span-full flex items-center gap-2 first:mt-0">
            <span class="i-lucide-layers size-3.5 text-woot-500" />
            <h3
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              Project Intelligence
            </h3>
            <div class="flex-1 h-px bg-n-weak" />
          </div>

          <!-- ── Project USP ── -->
          <KnowledgeSectionCard
            id="section-usp"
            icon="i-lucide-sparkles"
            title="Project USP"
            :status="saveStatus.usp"
            :last-saved-at="lastSavedAt.usp"
          >
            <textarea
              v-model="kb.usp"
              rows="3"
              placeholder="What makes this project unique — the one-line pitch."
              class="w-full flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500 resize-y"
              @input="onFieldInput('usp')"
            />
            <p class="text-[11px] text-n-slate-6 text-right">
              {{ charCount(kb.usp) }} characters
            </p>
          </KnowledgeSectionCard>

          <!-- ── Builder Story ── -->
          <KnowledgeSectionCard
            id="section-builder-story"
            icon="i-lucide-hard-hat"
            title="Builder Story"
            :status="saveStatus.builder_story"
            :last-saved-at="lastSavedAt.builder_story"
          >
            <input
              v-model="kb.builder_name"
              type="text"
              placeholder="Builder / developer name"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
              @input="onFieldInput('builder_story')"
            />
            <textarea
              v-model="kb.builder_track_record"
              rows="3"
              placeholder="Track record — past projects delivered, years in business, credibility markers."
              class="w-full flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500 resize-y"
              @input="onFieldInput('builder_story')"
            />
            <p class="text-[11px] text-n-slate-6 text-right">
              {{ charCount(kb.builder_track_record) }} characters
            </p>
          </KnowledgeSectionCard>

          <!-- ── Construction Highlights ── -->
          <KnowledgeSectionCard
            id="section-construction"
            icon="i-lucide-construction"
            title="Construction Highlights"
            :status="saveStatus.construction_highlights"
            :last-saved-at="lastSavedAt.construction_highlights"
          >
            <textarea
              v-model="kb.construction_highlights"
              rows="3"
              placeholder="Construction quality, materials, structural highlights."
              class="w-full flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500 resize-y"
              @input="onFieldInput('construction_highlights')"
            />
            <p class="text-[11px] text-n-slate-6 text-right">
              {{ charCount(kb.construction_highlights) }} characters
            </p>
          </KnowledgeSectionCard>

          <!-- ══ Group: Location Intelligence ══ -->
          <div class="col-span-full flex items-center gap-2 mt-2">
            <span class="i-lucide-map-pin size-3.5 text-woot-500" />
            <h3
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              Location Intelligence
            </h3>
            <div class="flex-1 h-px bg-n-weak" />
            <button
              class="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-woot-600 dark:text-woot-400 border border-n-weak hover:bg-woot-50 dark:hover:bg-woot-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isRecomputingRoutes"
              title="Fetches real driving-route distance/time from each place with coordinates to the project (needs the project's own coordinates set under Settings > Project > Edit)"
              @click="recomputeRoutes"
            >
              <span
                class="size-3"
                :class="
                  isRecomputingRoutes
                    ? 'i-lucide-loader-2 animate-spin'
                    : 'i-lucide-route'
                "
              />
              {{ isRecomputingRoutes ? 'Calculating…' : 'Recalculate Routes' }}
            </button>
          </div>
          <p
            v-if="recomputeRoutesError"
            class="col-span-full text-xs text-red-600 dark:text-red-400 -mt-2"
          >
            {{ recomputeRoutesError }}
          </p>

          <!-- ── Nearby Places (grouped by category) ── -->
          <KnowledgeSectionCard
            v-for="cat in NEARBY_CATEGORIES"
            :id="`section-nearby-${cat.value}`"
            :key="cat.value"
            v-model:collapsed="collapsedState[`nearby_${cat.value}`]"
            :icon="cat.icon"
            :title="cat.label"
            :status="saveStatus.nearby_places"
            :last-saved-at="lastSavedAt.nearby_places"
            collapsible
            :item-count="placesFor(cat.value).length"
          >
            <template #actions>
              <button
                class="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-woot-600 dark:text-woot-400 hover:bg-woot-50 dark:hover:bg-woot-500/10 transition-colors"
                @click="addPlace(cat.value)"
              >
                <span class="i-lucide-plus size-3" />
                Add
              </button>
            </template>
            <div
              v-if="!placesFor(cat.value).length"
              class="flex-1 flex items-center justify-center gap-1.5 text-xs text-n-slate-7 py-4"
            >
              <span class="i-lucide-map-pin size-3.5 text-n-slate-6" />
              None added yet.
            </div>
            <div v-else class="flex flex-col gap-2">
              <div
                v-for="place in placesFor(cat.value)"
                :key="place"
                class="flex flex-col gap-1.5 p-2 rounded-lg border border-n-weak"
              >
                <div class="flex items-center gap-2 flex-wrap">
                  <input
                    v-model="place.name"
                    type="text"
                    placeholder="Name"
                    class="flex-1 min-w-[8rem] px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
                    @input="onArrayChange('nearby_places')"
                  />
                  <input
                    v-model="place.distance_km"
                    type="text"
                    placeholder="Distance (km)"
                    class="w-24 px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
                    @input="onArrayChange('nearby_places')"
                  />
                  <input
                    v-model="place.travel_time_minutes"
                    type="text"
                    placeholder="Time (min)"
                    class="w-24 px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
                    @input="onArrayChange('nearby_places')"
                  />
                  <button
                    class="flex items-center justify-center w-7 h-7 rounded-lg text-n-slate-7 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 transition-colors shrink-0"
                    @click="removePlace(place)"
                  >
                    <span class="i-lucide-x size-3.5" />
                  </button>
                </div>
                <div class="flex items-center gap-2 flex-wrap">
                  <input
                    v-model="place.latitude"
                    type="text"
                    placeholder="Latitude"
                    class="w-28 px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
                    @input="onArrayChange('nearby_places')"
                  />
                  <input
                    v-model="place.longitude"
                    type="text"
                    placeholder="Longitude"
                    class="w-28 px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
                    @input="onArrayChange('nearby_places')"
                  />
                  <input
                    v-model="place.thumbnail_url"
                    type="text"
                    placeholder="Thumbnail image URL (optional)"
                    class="flex-1 min-w-[10rem] px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
                    @input="onArrayChange('nearby_places')"
                  />
                  <span
                    v-if="place.route_distance_km"
                    class="shrink-0 text-[11px] text-n-slate-7 whitespace-nowrap"
                  >
                    <span
                      class="i-lucide-route size-3 inline-block align-[-1px]"
                    />
                    {{ place.route_distance_km }} km ·
                    {{ place.route_duration_minutes }} min by road
                  </span>
                </div>
              </div>
            </div>
          </KnowledgeSectionCard>

          <!-- ══ Group: Sales Intelligence ══ -->
          <div class="col-span-full flex items-center gap-2 mt-2">
            <span class="i-lucide-line-chart size-3.5 text-woot-500" />
            <h3
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              Sales Intelligence
            </h3>
            <div class="flex-1 h-px bg-n-weak" />
          </div>

          <!-- ── Ideal Customer ── -->
          <KnowledgeSectionCard
            id="section-ideal-customer"
            icon="i-lucide-user-check"
            title="Ideal Customer"
            :status="saveStatus.target_customer"
            :last-saved-at="lastSavedAt.target_customer"
          >
            <textarea
              v-model="kb.target_customer"
              rows="3"
              placeholder="Who this project is built for — family type, income band, priorities."
              class="w-full flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500 resize-y"
              @input="onFieldInput('target_customer')"
            />
            <p class="text-[11px] text-n-slate-6 text-right">
              {{ charCount(kb.target_customer) }} characters
            </p>
          </KnowledgeSectionCard>

          <!-- ── Lifestyle ── -->
          <KnowledgeSectionCard
            id="section-lifestyle"
            icon="i-lucide-palmtree"
            title="Lifestyle"
            :status="saveStatus.lifestyle_notes"
            :last-saved-at="lastSavedAt.lifestyle_notes"
          >
            <textarea
              v-model="kb.lifestyle_notes"
              rows="3"
              placeholder="Amenities and lifestyle experience — clubhouse, sports, community."
              class="w-full flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500 resize-y"
              @input="onFieldInput('lifestyle_notes')"
            />
            <p class="text-[11px] text-n-slate-6 text-right">
              {{ charCount(kb.lifestyle_notes) }} characters
            </p>
          </KnowledgeSectionCard>

          <!-- ── Investment Highlights ── -->
          <KnowledgeSectionCard
            id="section-investment"
            icon="i-lucide-trending-up"
            title="Investment Highlights"
            :status="saveStatus.investment"
            :last-saved-at="lastSavedAt.investment"
          >
            <textarea
              v-model="kb.investment_notes"
              rows="3"
              placeholder="Appreciation potential, comparable pricing, growth corridor."
              class="w-full flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500 resize-y"
              @input="onFieldInput('investment')"
            />
            <textarea
              v-model="kb.rental_notes"
              rows="3"
              placeholder="Rental outlook — expected yield, demand drivers."
              class="w-full flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500 resize-y"
              @input="onFieldInput('investment')"
            />
            <p class="text-[11px] text-n-slate-6 text-right">
              {{ charCount(kb.investment_notes) + charCount(kb.rental_notes) }}
              characters
            </p>
          </KnowledgeSectionCard>

          <!-- ── FAQs ── -->
          <KnowledgeSectionCard
            id="section-faqs"
            v-model:collapsed="collapsedState.faqs"
            icon="i-lucide-circle-help"
            title="FAQs"
            :status="saveStatus.faqs"
            :last-saved-at="lastSavedAt.faqs"
            collapsible
            :item-count="kb.faqs.length"
          >
            <template #actions>
              <button
                class="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-woot-600 dark:text-woot-400 hover:bg-woot-50 dark:hover:bg-woot-500/10 transition-colors"
                @click="addFaq"
              >
                <span class="i-lucide-plus size-3" />
                Add FAQ
              </button>
            </template>
            <div
              v-if="!kb.faqs.length"
              class="flex-1 flex items-center justify-center gap-1.5 text-xs text-n-slate-7 py-4"
            >
              <span class="i-lucide-circle-help size-3.5 text-n-slate-6" />
              None added yet.
            </div>
            <div v-else class="flex flex-col gap-3">
              <div
                v-for="faq in kb.faqs"
                :key="faq"
                class="p-3 rounded-lg border border-n-weak bg-n-solid-1 flex flex-col gap-2"
              >
                <div class="flex items-center gap-2">
                  <input
                    v-model="faq.category"
                    type="text"
                    placeholder="Category (optional)"
                    class="flex-1 px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
                    @input="onArrayChange('faqs')"
                  />
                  <button
                    class="flex items-center justify-center w-7 h-7 rounded-lg text-n-slate-7 hover:bg-ruby-50 hover:text-ruby-600 dark:hover:bg-ruby-500/10 transition-colors shrink-0"
                    @click="removeFaq(faq)"
                  >
                    <span class="i-lucide-x size-3.5" />
                  </button>
                </div>
                <input
                  v-model="faq.question"
                  type="text"
                  placeholder="Question"
                  class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
                  @input="onArrayChange('faqs')"
                />
                <textarea
                  v-model="faq.answer"
                  rows="3"
                  placeholder="Answer"
                  class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500 resize-y"
                  @input="onArrayChange('faqs')"
                />
                <p class="text-[11px] text-n-slate-6 text-right -mt-1">
                  {{ charCount(faq.answer) }} characters
                </p>
              </div>
            </div>
          </KnowledgeSectionCard>

          <!-- ── Common Objections ── -->
          <KnowledgeSectionCard
            id="section-objections"
            v-model:collapsed="collapsedState.objection_responses"
            icon="i-lucide-shield-question"
            title="Common Objections"
            :status="saveStatus.objection_responses"
            :last-saved-at="lastSavedAt.objection_responses"
            collapsible
            :item-count="kb.objection_responses.length"
          >
            <template #actions>
              <button
                class="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-woot-600 dark:text-woot-400 hover:bg-woot-50 dark:hover:bg-woot-500/10 transition-colors"
                @click="addObjection"
              >
                <span class="i-lucide-plus size-3" />
                Add Objection
              </button>
            </template>
            <div
              v-if="!kb.objection_responses.length"
              class="flex-1 flex items-center justify-center gap-1.5 text-xs text-n-slate-7 py-4"
            >
              <span class="i-lucide-shield-question size-3.5 text-n-slate-6" />
              None added yet.
            </div>
            <div v-else class="flex flex-col gap-3">
              <div
                v-for="obj in kb.objection_responses"
                :key="obj"
                class="p-3 rounded-lg border border-n-weak bg-n-solid-1 flex flex-col gap-2"
              >
                <div class="flex items-center gap-2">
                  <input
                    v-model="obj.objection_type"
                    type="text"
                    placeholder="Objection (e.g. Price too high)"
                    class="flex-1 px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
                    @input="onArrayChange('objection_responses')"
                  />
                  <button
                    class="flex items-center justify-center w-7 h-7 rounded-lg text-n-slate-7 hover:bg-ruby-50 hover:text-ruby-600 dark:hover:bg-ruby-500/10 transition-colors shrink-0"
                    @click="removeObjection(obj)"
                  >
                    <span class="i-lucide-x size-3.5" />
                  </button>
                </div>
                <textarea
                  v-model="obj.response"
                  rows="3"
                  placeholder="Recommended response"
                  class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500 resize-y"
                  @input="onArrayChange('objection_responses')"
                />
                <p class="text-[11px] text-n-slate-6 text-right -mt-1">
                  {{ charCount(obj.response) }} characters
                </p>
              </div>
            </div>
          </KnowledgeSectionCard>

          <!-- ── Sales Talking Points ── -->
          <KnowledgeSectionCard
            id="section-talking-points"
            v-model:collapsed="collapsedState.talking_points"
            icon="i-lucide-message-square-text"
            title="Sales Talking Points"
            :status="saveStatus.talking_points"
            :last-saved-at="lastSavedAt.talking_points"
            collapsible
            :item-count="kb.talking_points.length"
          >
            <template #actions>
              <button
                class="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-woot-600 dark:text-woot-400 hover:bg-woot-50 dark:hover:bg-woot-500/10 transition-colors"
                @click="addTalkingPoint"
              >
                <span class="i-lucide-plus size-3" />
                Add Point
              </button>
            </template>
            <div
              v-if="!kb.talking_points.length"
              class="flex-1 flex items-center justify-center gap-1.5 text-xs text-n-slate-7 py-4"
            >
              <span
                class="i-lucide-message-square-text size-3.5 text-n-slate-6"
              />
              None added yet.
            </div>
            <div v-else class="flex flex-col gap-2">
              <div
                v-for="(point, idx) in kb.talking_points"
                :key="idx"
                class="flex items-center gap-2"
              >
                <input
                  v-model="kb.talking_points[idx]"
                  type="text"
                  placeholder="Talking point"
                  class="flex-1 px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
                  @input="onArrayChange('talking_points')"
                />
                <button
                  class="flex items-center justify-center w-7 h-7 rounded-lg text-n-slate-7 hover:bg-ruby-50 hover:text-ruby-600 dark:hover:bg-ruby-500/10 transition-colors shrink-0"
                  @click="removeTalkingPoint(idx)"
                >
                  <span class="i-lucide-x size-3.5" />
                </button>
              </div>
            </div>
          </KnowledgeSectionCard>

          <!-- ── Competitor Notes ── -->
          <KnowledgeSectionCard
            id="section-competitor"
            icon="i-lucide-swords"
            title="Competitor Notes"
            :status="saveStatus.competitor_notes"
            :last-saved-at="lastSavedAt.competitor_notes"
          >
            <textarea
              v-model="kb.competitor_notes"
              rows="3"
              placeholder="Nearby competing projects and how this one compares — internal use only."
              class="w-full flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500 resize-y"
              @input="onFieldInput('competitor_notes')"
            />
            <p class="text-[11px] text-n-slate-6 text-right">
              {{ charCount(kb.competitor_notes) }} characters
            </p>
          </KnowledgeSectionCard>

          <!-- ══ Group: Legal Intelligence ══ -->
          <div class="col-span-full flex items-center gap-2 mt-2">
            <span class="i-lucide-scale size-3.5 text-woot-500" />
            <h3
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              Legal Intelligence
            </h3>
            <div class="flex-1 h-px bg-n-weak" />
          </div>

          <!-- ── Legal Notes ── -->
          <KnowledgeSectionCard
            id="section-legal"
            icon="i-lucide-scale"
            title="Legal Notes"
            :status="saveStatus.legal_notes"
            :last-saved-at="lastSavedAt.legal_notes"
          >
            <textarea
              v-model="kb.legal_notes"
              rows="3"
              placeholder="RERA number, approvals on file — factual only, never a legal guarantee."
              class="w-full flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500 resize-y"
              @input="onFieldInput('legal_notes')"
            />
            <p class="text-[11px] text-n-slate-6 text-right">
              {{ charCount(kb.legal_notes) }} characters
            </p>
          </KnowledgeSectionCard>
        </div>
      </div>
    </div>
  </ProjectWorkspaceLayout>
</template>
